/**
 * Admin Pro Imports API client.
 *
 * Wraps the 6 admin endpoints under `/api/admin/imports/*` and the
 * enqueue endpoint at `/api/admin/pros/:proId/imports`.
 */

import { request } from "../base";

export type ProImportStatus =
	| "queued"
	| "running"
	| "completed"
	| "failed"
	| "cancelled";

export interface ProImportListItem {
	id: string;
	proId: string;
	proBusinessName: string | null;
	sourceUrl: string;
	status: ProImportStatus;
	statsJson: {
		projectsFound?: number;
		photosDownloaded?: number;
		costCents?: number;
		manifestKey?: string;
	} | null;
	errorCode: string | null;
	errorMessage: string | null;
	startedAt: number | null;
	completedAt: number | null;
	createdAt: number;
	approvedAt: number | null;
	submittedBy: string | null;
}

export interface ProImportDetail extends ProImportListItem {
	sourceType: string;
	consentAt: number;
	idempotencyKey: string;
	approvedBy: string | null;
}

export interface ScrapedPhoto {
	file: string;
	originalUrl: string;
	caption: string;
	isCover: boolean;
}

export interface ScrapedProject {
	title: string;
	slug: string;
	description: string;
	propertyTypeRaw: string;
	budgetRangeRaw: string;
	locationRaw: string;
	yearCompleted: number | null;
	durationRaw: string;
	photos: ScrapedPhoto[];
}

export interface ScrapedProfile {
	businessName: string;
	slug: string;
	sourcePlatform: string;
	sourceUrl: string;
	whatsapp: string;
	phoneAlternate: string;
	email: string;
	tagline: string;
	about: string;
	cityRaw: string;
	addressLine1: string;
	addressCity: string;
	addressState: string;
	addressPincode: string;
	teamSizeRaw: string;
	yearsInBusiness: number | null;
	serviceAreasRaw: string[];
	credentialsRaw: string[];
	reviewRating: number | null;
	reviewCount: number | null;
	typicalCostRaw: string;
	logoUrl: string;
	coverImage: string;
}

export type YamlParsed =
	| { ok: true; data: { pro: ScrapedProfile; projects: ScrapedProject[] } }
	| { ok: false; errors: string[] };

export interface TaxonomyMatch {
	id: string | null;
	score: number;
	candidate: string;
	recommendation: "auto" | "review" | "unknown";
	alternatives: Array<{ id: string; name: string; score: number }>;
}

export interface TaxonomySuggestions {
	city: TaxonomyMatch | null;
	serviceAreas: TaxonomyMatch[];
	materials: TaxonomyMatch[];
}

export interface ProImportDetailResponse {
	import: ProImportDetail;
	yamlParsed: YamlParsed | null;
	taxonomy: TaxonomySuggestions | null;
}

export interface ProImportEvent {
	id: string;
	importId: string;
	kind: string;
	payloadJson: Record<string, unknown> | null;
	createdAt: number;
}

export interface ProImportEventsResponse {
	events: ProImportEvent[];
	cursor: number;
}

export interface MaterializeSelection {
	profile: { fields: string[] };
	projects: Array<{
		scrapedSlug: string;
		include: boolean;
		photos: string[];
		overrides?: Partial<{ title: string; description: string }>;
	}>;
}

export interface MaterializeResult {
	profileFieldsApplied: number;
	projectsCreated: number;
	photosCopied: number;
	skipped: Array<{ reason: string; slug: string }>;
}

export const adminImportsApi = {
	async list(filters?: { proId?: string; status?: ProImportStatus }) {
		const params = new URLSearchParams();
		if (filters?.proId) params.set("proId", filters.proId);
		if (filters?.status) params.set("status", filters.status);
		const qs = params.toString() ? `?${params.toString()}` : "";
		return request<{ imports: ProImportListItem[] }>(
			`/api/admin/imports${qs}`,
		);
	},

	async get(importId: string) {
		return request<ProImportDetailResponse>(`/api/admin/imports/${importId}`);
	},

	async events(importId: string, since?: number) {
		const qs = since ? `?since=${since}` : "";
		return request<ProImportEventsResponse>(
			`/api/admin/imports/${importId}/events${qs}`,
		);
	},

	async enqueue(
		proId: string,
		body: { sourceUrl: string; consentAt?: number; force?: boolean },
	) {
		return request<{ importId: string; status: ProImportStatus }>(
			`/api/admin/pros/${proId}/imports`,
			{ method: "POST", body },
		);
	},

	async approve(importId: string, selection: MaterializeSelection) {
		return request<MaterializeResult>(
			`/api/admin/imports/${importId}/approve`,
			{ method: "POST", body: { selection } },
		);
	},

	async bulkApprove(importIds: string[]) {
		return request<{
			results: Array<{
				importId: string;
				ok: boolean;
				result?: MaterializeResult;
				error?: string;
			}>;
		}>(`/api/admin/imports/bulk-approve`, {
			method: "POST",
			body: { importIds },
		});
	},

	async cancel(importId: string) {
		return request<{ id: string; status: ProImportStatus }>(
			`/api/admin/imports/${importId}/cancel`,
			{ method: "POST" },
		);
	},
};
